import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React, { useEffect, useRef, useState } from 'react';
import {
  MasterCropData,
  SaveCheckType,
  SaveWarning,
  SaveWarningEvidenceCrop,
  SaveWarningReviewOutcome,
} from '../../types';
import { buildMasterLabelUrl } from '../../utils/masterLabelLinks';
import { CropThumbnail } from './CropThumbnail';
import { MasterLabelLink } from './MasterLabelLink';

export interface SaveWarningDialogProps {
  warnings: SaveWarning[];
  onAcknowledge: (comment: string, outcome: SaveWarningReviewOutcome) => void;
  onGoBack: () => void;
  saving?: boolean;
}

const SAVE_CHECK_TYPE_LABELS: Record<SaveCheckType, string> = {
  crop_placement: 'Crop placement',
  design_group: 'Design group',
};

const REVIEW_OUTCOME_OPTIONS: ReadonlyArray<{
  value: SaveWarningReviewOutcome;
  label: string;
  description: string;
}> = [
  {
    value: 'correct_warning',
    label: 'Valid warning',
    description: 'The check correctly identified a suspicious change.',
  },
  {
    value: 'false_positive',
    label: 'False positive',
    description: 'The change is correct and this warning should not have fired.',
  },
  {
    value: 'unsure',
    label: 'Unsure',
    description: 'There is not enough evidence to judge the check.',
  },
];

function cropForEvidence(evidence: SaveWarningEvidenceCrop): MasterCropData {
  return { id: evidence.cropId, imageUrl: evidence.vizUrl ?? '' };
}

function formatSimilarity(similarity: number | null): string | null {
  if (similarity === null) return null;
  return `${Math.round(similarity * 100)}%`;
}

/** The warning's *matched* item's Master Label deep link: the backend-supplied URL, or a
 * fallback built from the matched item number with the subject's crops highlighted. Returns
 * null when there's nothing to link to (e.g. a check with no matched item). */
function matchedMasterLabelUrlForWarning(warning: SaveWarning): string | null {
  if (warning.matchedMasterLabelUrl) return warning.matchedMasterLabelUrl;
  if (!warning.matchedItemNumber) return null;
  return buildMasterLabelUrl({
    chainId: warning.chainId,
    itemNumber: warning.matchedItemNumber,
    cropIds: warning.subjectCropIds,
  });
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.56)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1150,
  padding: 20,
};

const cardStyle: React.CSSProperties = {
  width: 'min(720px, 96vw)',
  maxHeight: 'calc(100vh - 64px)',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-elevated)',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const warningBlockStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg)',
};

/** Presentational save-check warning dialog: shown before a save proceeds when one or more
 * save checks fired. Renders the warnings with their evidence, then requires a comment before
 * the caller may acknowledge and continue. Has no data fetching or save logic of its own — the
 * caller supplies `warnings` and handles `onAcknowledge`/`onGoBack`. */
export const SaveWarningDialog: React.FC<SaveWarningDialogProps> = ({
  warnings,
  onAcknowledge,
  onGoBack,
  saving = false,
}) => {
  const [comment, setComment] = useState('');
  const [reviewOutcome, setReviewOutcome] = useState<SaveWarningReviewOutcome | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const warningSetKey = warnings.map((warning) => warning.warningId).join('\u0000');

  useEffect(() => {
    setComment('');
    setReviewOutcome(null);
    textareaRef.current?.focus();
  }, [warningSetKey]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) {
        event.preventDefault();
        event.stopPropagation();
        onGoBack();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onGoBack, saving]);

  const trimmedComment = comment.trim();
  const canAcknowledge = trimmedComment.length > 0 && reviewOutcome !== null && !saving;

  const handleGoBack = () => {
    if (saving) return;
    onGoBack();
  };

  const handleAcknowledge = () => {
    if (!canAcknowledge || reviewOutcome === null) return;
    onAcknowledge(trimmedComment, reviewOutcome);
  };

  const issueCount = warnings.length;

  return (
    <div data-testid="save-warning-dialog-overlay" style={overlayStyle} onClick={handleGoBack}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-warning-dialog-title"
        onClick={(event) => event.stopPropagation()}
        style={cardStyle}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid rgba(245, 158, 11, 0.32)',
            background: 'var(--warning-subtle)',
          }}
        >
          <WarningAmberOutlinedIcon style={{ color: 'var(--warning)', fontSize: 20 }} />
          <div
            id="save-warning-dialog-title"
            style={{ fontSize: 14, fontWeight: 800, color: 'var(--warning)' }}
          >
            {issueCount} possible issue{issueCount === 1 ? '' : 's'} with this save
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {warnings.map((warning) => {
            const matchedUrl = matchedMasterLabelUrlForWarning(warning);
            return (
              <div
                key={warning.warningId}
                data-testid="save-warning-block"
                style={warningBlockStyle}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  {SAVE_CHECK_TYPE_LABELS[warning.checkType]}
                </span>
                <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{warning.message}</div>
                {warning.evidenceCrops.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {warning.evidenceCrops.map((evidence) => {
                      const similarityLabel = formatSimilarity(evidence.similarity);
                      return (
                        <div
                          key={evidence.cropId}
                          style={{ display: 'grid', gap: 2, justifyItems: 'center' }}
                        >
                          <CropThumbnail
                            crop={cropForEvidence(evidence)}
                            sectionType="front"
                            size={48}
                          />
                          {similarityLabel ? (
                            <span
                              style={{
                                fontSize: 9,
                                color: 'var(--text-tertiary)',
                                fontFamily: 'var(--font-mono)',
                              }}
                            >
                              {similarityLabel}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
                {matchedUrl ? (
                  <MasterLabelLink to={matchedUrl} variant="forge">
                    View matching item in Master Label
                  </MasterLabelLink>
                ) : null}
              </div>
            );
          })}
        </div>

        <fieldset
          disabled={saving}
          style={{
            margin: 0,
            padding: 0,
            border: 0,
            display: 'grid',
            gap: 6,
          }}
        >
          <legend
            style={{
              marginBottom: 6,
              color: 'var(--text-primary)',
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            How useful was this warning{issueCount === 1 ? '' : ' set'}?
          </legend>
          {REVIEW_OUTCOME_OPTIONS.map((option) => (
            <label
              key={option.value}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '2px 8px',
                alignItems: 'start',
                padding: '8px 10px',
                border: `1px solid ${
                  reviewOutcome === option.value ? 'var(--accent-border)' : 'var(--border)'
                }`,
                borderRadius: 'var(--radius-sm)',
                background: reviewOutcome === option.value ? 'var(--accent-subtle)' : 'var(--bg)',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="radio"
                name="save-warning-review-outcome"
                value={option.value}
                checked={reviewOutcome === option.value}
                onChange={() => setReviewOutcome(option.value)}
                style={{ marginTop: 2 }}
              />
              <span>
                <span
                  style={{
                    display: 'block',
                    color: 'var(--text-primary)',
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  {option.label}
                </span>
                <span style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: 10 }}>
                  {option.description}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        <label
          htmlFor="save-warning-dialog-comment"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontSize: 11,
            color: 'var(--text-tertiary)',
          }}
        >
          Explain your review and why you are continuing
          <textarea
            id="save-warning-dialog-comment"
            ref={textareaRef}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            disabled={saving}
            required
            rows={3}
            style={{
              resize: 'vertical',
              padding: '8px 10px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-primary)',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          />
        </label>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleGoBack}
            disabled={saving}
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text-primary)',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            Go back
          </button>
          <button
            type="button"
            onClick={handleAcknowledge}
            disabled={!canAcknowledge}
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--accent-border)',
              background: canAcknowledge ? 'var(--accent)' : 'var(--border)',
              color: canAcknowledge ? 'var(--on-accent, #fff)' : 'var(--text-tertiary)',
              cursor: canAcknowledge ? 'pointer' : 'not-allowed',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            Review & save
          </button>
        </div>
      </div>
    </div>
  );
};
